/* Signet Autocare — scroll reveals.
   Loaded from layout/theme.liquid, defer.

   Design notes, because the failure modes here are all "shop looks broken":

   1. NOTHING IS HIDDEN BY CSS ALONE. This script adds `.signet-motion` to
      <html> first; only then does the stylesheet hide reveal targets. Script
      blocked or throwing => everything simply stays visible.

   2. A FAILSAFE TIMER reveals every target after 2.5s no matter what the
      observer did. If IntersectionObserver misbehaves inside a section the
      theme lazy-renders, the worst case is content appearing without an
      animation — never content that stays invisible.

   3. TARGETS ARE CONTENT ONLY. Header, nav and footer are never touched;
      animating navigation is how you get a menu that is invisible when
      someone needs it.

   4. Respects prefers-reduced-motion by not running at all. */

(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches) return;
  if (!('IntersectionObserver' in window)) return;

  var root = document.documentElement;
  var SELECTOR = [
    '#MainContent product-card',
    '#MainContent .product-card',
    '#MainContent .shopify-section > h1',
    '#MainContent .shopify-section > h2',
    '#MainContent .collection-list__item',
    '#MainContent .card-gallery'
  ].join(',');

  function collect() {
    var main = document.getElementById('MainContent');
    if (!main) return [];
    return Array.prototype.slice.call(main.querySelectorAll(SELECTOR))
      .filter(function (el) {
        // never animate anything living inside site chrome
        return !el.closest('header, footer, nav');
      });
  }

  function revealAll(els) {
    els.forEach(function (el) { el.classList.add('signet-in'); });
  }

  function start() {
    var els = collect();
    if (!els.length) return;

    root.classList.add('signet-motion');

    els.forEach(function (el) { el.setAttribute('data-signet-reveal', ''); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        // Stagger siblings so a grid cascades instead of snapping in as one
        // block. Capped so a long grid never feels slow.
        var sibs = el.parentElement
          ? Array.prototype.indexOf.call(el.parentElement.children, el)
          : 0;
        el.style.setProperty('--signet-delay', Math.min(sibs, 6) * 70 + 'ms');
        el.classList.add('signet-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    els.forEach(function (el) { io.observe(el); });

    // Failsafe: whatever happened above, show everything.
    setTimeout(function () { revealAll(els); }, 2500);
  }

  /* Hero video background.
     Injected rather than put in the theme so the whole feature lives in two
     asset files. Decorative only: muted, looping, no controls, and it never
     carries information. If the file 404s or the browser refuses to autoplay,
     the hero is simply the hero -- nothing is hidden behind it. */
  /* Two formats on purpose. VP9/WebM first because every Chromium build can
     decode it, then H.264/MP4 for Safari and iOS. Shipping MP4 alone left the
     clip stuck at readyState 0 in a Chromium without proprietary codecs --
     silently, with no media error to catch. */
  var HERO_SOURCES = [
    ['https://cdn.shopify.com/s/files/1/1072/4556/8330/files/hero_bg.webm',
     'video/webm'],
    ['https://cdn.shopify.com/s/files/1/1072/4556/8330/files/hero_bg.mp4',
     'video/mp4']
  ];

  function heroVideo() {
    if (reduce.matches) return;                 // asked for less motion
    var main = document.getElementById('MainContent');
    if (!main) return;
    var hero = main.querySelector('.shopify-section');
    if (!hero || hero.querySelector('.signet-hero-video')) return;
    // Only dress a hero that is tall enough to be one; never a thin bar.
    if (hero.getBoundingClientRect().height < 260) return;

    var v = document.createElement('video');
    v.className = 'signet-hero-video';
    HERO_SOURCES.forEach(function (pair) {
      var s = document.createElement('source');
      s.src = pair[0];
      s.type = pair[1];
      v.appendChild(s);
    });
    v.autoplay = true;
    v.loop = true;
    v.muted = true;              // required for autoplay on every browser
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.setAttribute('tabindex', '-1');
    v.preload = 'auto';

    // If it cannot play, take it back out rather than leave a black box.
    v.addEventListener('error', function () {
      hero.classList.remove('signet-has-hero-video');
      if (v.parentNode) v.parentNode.removeChild(v);
    });

    hero.classList.add('signet-has-hero-video');
    hero.insertBefore(v, hero.firstChild);
    var p = v.play();
    if (p && p.catch) p.catch(function () { /* autoplay blocked: harmless */ });

    /* Watchdog. I could not verify playback in the automated browser used to
       build this -- media never starts there, for either WebM or MP4, so
       "it works" was unprovable at build time. Rather than trust it, the clip
       is given 6 seconds to produce actual frames; if it has not, it is taken
       back out and the hero returns to exactly what it was. Better a hero
       with no video than a hero with a dead element in it. */
    setTimeout(function () {
      if (v.readyState === 0 || v.videoWidth === 0) {
        hero.classList.remove('signet-has-hero-video');
        if (v.parentNode) v.parentNode.removeChild(v);
      }
    }, 6000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      start();
      heroVideo();
    });
  } else {
    start();
    heroVideo();
  }

  // Horizon swaps sections in without a page load (filtering, pagination).
  // Re-run so newly injected cards animate too rather than sitting hidden.
  document.addEventListener('shopify:section:load', start);
})();
